import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Share2, Check, Copy, Trash2, Loader2, Pencil, Flame, Eye, Calendar, MessageCircle, X, MoreVertical, Users, Flag, Pin, EyeOff, Globe, Link2, ArrowUp, ArrowDown, Archive, ArchiveRestore } from 'lucide-react';
import { BACKEND_URL, deletePrayer, updatePrayer, lightCandle, getCircle, toggleCircle, getPinnedStatus, pinPrayer, unpinPrayer } from '../lib/api';
import { sharePrayer, prayerShareUrl } from '../lib/share';
import { useAuth } from '../contexts/AuthContext';
import ReportPrayerButton from './ReportPrayerButton';
import OwnerCommentsDialog from './OwnerCommentsDialog';
import { useConfirmDialog } from './ConfirmDialog';
import { bgGradient, readableTextOn, readableTextShadow } from '../lib/color';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';

const fmtDur = (s) => {
  const m = Math.floor((s || 0) / 60);
  const sec = Math.floor((s || 0) % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

// Compact number formatter for metadata chips: 12 → "12", 1250 → "1.2k",
// 2_000_000 → "2M". Keeps the metadata row visually tight even for popular
// prayers with high view / candle counts.
const fmtCount = (n) => {
  const v = Number(n) || 0;
  if (v < 1000) return String(v);
  if (v < 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  if (v < 1_000_000) return `${Math.round(v / 1000)}k`;
  if (v < 10_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  return `${Math.round(v / 1_000_000)}M`;
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff/86400)}d ago`;
  return d.toLocaleDateString();
};

// `onDelete(id)` is optional — the parent may pass it to remove the card
// from local state instantly. If omitted we do a soft page reload so the
// caller doesn't have to plumb state everywhere.
// `onRemove(prayer)` is optional — when provided, an X icon renders on the
// top-left of the thumbnail. Clicking it calls the handler so the parent
// can remove the card from a curated list (e.g. the Shared tab ledger).
// `sharedContextRole` is optional — pass 'authored' when the current
// viewer created this random-share, or 'received' when someone else's
// random-share landed with them. When set, the cover badge switches from
// the generic "Selected Randomly" pill to a role-specific label ("You
// sent to a random member" / "Received Randomly") so the Shared / Random
// / Nearby / Search tabs can distinguish outgoing vs incoming random
// shares at a glance.
// `showGenericRandomPill` — when true, an unrelated random-share (viewer
// is neither author nor recipient) still renders the generic "Selected
// Randomly" pill. Kept OFF by default so Nearby / Shared / Search never
// tag a random-share prayer that has nothing to do with the viewer. Only
// the Random tab flips this on so users can see WHY that card was picked.
// `showMoveControls` — when true, the ⋮ dropdown gains Move Up / Move
// Down items so the viewer can manually re-order the card within its
// section on the Circle page. Wired by CirclePage for the owner's own
// self-view. `canMoveUp` / `canMoveDown` gate each direction so we
// don't render a click that would just no-op at the boundary. The
// actual reorder logic (which array to reshuffle, which persistence
// endpoint to hit) is owned by the parent via `onMoveUp` / `onMoveDown`.
const PrayerCard = ({ prayer, onDelete, onUpdate, onRemove, sharedContextRole = null, showGenericRandomPill = false, onDismiss = null, isPinned = false, showMoveControls = false, canMoveUp = false, canMoveDown = false, onMoveUp = null, onMoveDown = null }) => {
  const { user, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const isOwner = Boolean(user && user.user_id === prayer.user_id);
  const isAdmin = Boolean(user && user.role === 'admin');
  // Reveal delete on system-generated seed prayers so ephemeral test data
  // (created by regression runs, titled "TEST_…") is easy to clean up from
  // the UI. Admins can delete ANY prayer for moderation. Backend enforces
  // the same rules for the actual delete call.
  const isTestPrayer = ((prayer.title || '').trim().toLowerCase().startsWith('test_'));
  const canDelete = Boolean(user) && (isOwner || isTestPrayer || isAdmin);
  const imageSrc = prayer.image_url ? `${BACKEND_URL}${prayer.image_url}` : null;
  const hasAudio = Boolean(prayer.audio_url);
  const [shareStatus, setShareStatus] = useState(null); // 'shared' | 'copied' | null
  const [copyLinkStatus, setCopyLinkStatus] = useState(null); // 'copied' | 'error' | null
  const copyLinkTimerRef = useRef(null);
  const [deleting, setDeleting] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsCount, setCommentsCount] = useState(Number(prayer.owner_comments_count) || 0);
  const [deleted, setDeleted] = useState(false);
  // Candle toggle state — seeded from the server so the icon reflects the
  // viewer's current lit/unlit status on first render. Kept local so the
  // card can flip instantly on click without a parent-level refetch.
  const [candlesCount, setCandlesCount] = useState(Number(prayer.candles_count) || 0);
  const [isCandleLit, setIsCandleLit] = useState(Boolean(prayer.is_candle_lit_by_viewer));
  const [candleBusy, setCandleBusy] = useState(false);
  const statusTimerRef = useRef(null);
  // ---- Menu-level circle status ----
  // The metadata-row `JoinCircleButton` manages its own state. This
  // parallel state powers the "Join Circle" row inside the ⋮ dropdown
  // so a user can toggle their circle membership from the menu too.
  // Loaded lazily the first time the menu opens to avoid an extra
  // status fetch on every card.
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [circleLoaded, setCircleLoaded] = useState(false);
  // Pin state for the "Pin onto my circle" menu row. Fetched lazily on
  // first menu open (mirrors the Join Circle status pattern) so we
  // don't spam an extra request on every card. Own-prayer skips the
  // fetch since the menu item is hidden anyway.
  const [pinLoaded, setPinLoaded] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [circleInCircle, setCircleInCircle] = useState(false);
  const [circleIsSelf, setCircleIsSelf] = useState(Boolean(user && prayer.user_id && user.user_id === prayer.user_id));
  const [circleBusy, setCircleBusy] = useState(false);

  // Custom branded confirmation dialog — replaces the native
  // `window.confirm()` that previously popped for the delete flow.
  // Matches the same look/feel used by ManagePrayers + PrayerHistory.
  const { confirm: confirmDialog, dialog: confirmDialogEl } = useConfirmDialog();

  // Re-sync candle state whenever the server-side flags change (e.g., a
  // feed refetch swaps in a fresh prayer object). Local optimistic
  // updates still win in between refetches.
  useEffect(() => {
    setCandlesCount(Number(prayer.candles_count) || 0);
    setIsCandleLit(Boolean(prayer.is_candle_lit_by_viewer));
  }, [prayer.id, prayer.candles_count, prayer.is_candle_lit_by_viewer]);

  useEffect(() => () => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    if (copyLinkTimerRef.current) clearTimeout(copyLinkTimerRef.current);
  }, []);

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Fire the OS share sheet directly — no intermediate prompt.
    const result = await sharePrayer(prayer, user);
    if (result === 'cancelled') return;
    setShareStatus(result === 'error' ? 'error' : result);
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    statusTimerRef.current = setTimeout(() => setShareStatus(null), 1800);
  };

  // Copy just the shareable URL to the clipboard (no OS share sheet).
  // Uses the same URL builder as sharePrayer so the recipient still
  // lands via ?ref=share&shared_by=... and the sharer is credited.
  const handleCopyLink = async () => {
    const url = prayerShareUrl(prayer, user);
    if (!url) {
      setCopyLinkStatus('error');
      if (copyLinkTimerRef.current) clearTimeout(copyLinkTimerRef.current);
      copyLinkTimerRef.current = setTimeout(() => setCopyLinkStatus(null), 1800);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopyLinkStatus('copied');
    } catch (err) {
      console.warn('copy link failed', err);
      setCopyLinkStatus('error');
    }
    if (copyLinkTimerRef.current) clearTimeout(copyLinkTimerRef.current);
    copyLinkTimerRef.current = setTimeout(() => setCopyLinkStatus(null), 1800);
  };

  const handleToggleCandle = async (e) => {
    // The Flame lives inside the wrapping <Link>, so cancel the default
    // navigation and stop the click from bubbling to the anchor.
    e.preventDefault();
    e.stopPropagation();
    if (candleBusy) return;
    if (!user) { openAuthModal(); return; }
    setCandleBusy(true);
    const willLight = !isCandleLit;
    setIsCandleLit(willLight);
    setCandlesCount((n) => Math.max(0, n + (willLight ? 1 : -1)));
    try {
      const res = await lightCandle(prayer.id);
      if (res && typeof res.candles_count === 'number') setCandlesCount(res.candles_count);
      if (typeof res?.is_lit === 'boolean') setIsCandleLit(res.is_lit);
    } catch (err) {
      console.error('candle toggle failed', err);
      setIsCandleLit(!willLight);
      setCandlesCount((n) => Math.max(0, n + (willLight ? -1 : 1)));
    } finally {
      setCandleBusy(false);
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const label = prayer.title || 'this prayer';
    const isAdminDelete = isAdmin && !isOwner;
    const ok = await confirmDialog({
      title: isAdminDelete ? 'Admin delete this prayer?' : 'Delete this prayer?',
      description: isAdminDelete
        ? `Remove "${label}" by @${prayer.user_handle || prayer.user_name || 'user'}? This will hide it from every public feed. This cannot be undone.`
        : `"${label}" will be permanently removed. This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deletePrayer(prayer.id);
      setDeleted(true);
      if (typeof onDelete === 'function') {
        onDelete(prayer.id);
      } else {
        // Fallback for callers that don't manage the list themselves.
        setTimeout(() => window.location.reload(), 400);
      }
    } catch (err) {
      console.error('delete failed', err);
      setDeleting(false);
      alert('Could not delete this prayer. Please try again.');
    }
  };

  // Owner-only visibility toggle — flips the prayer between "public"
  // (appears on Home feed tabs) and "unlisted" (only reachable via
  // direct link). Same PATCH endpoint the Manage Prayers page and the
  // Edit form use, so moderation + audit-trail behaviour is unchanged.
  // Local `unlistBusy` guards against double-clicks while the request
  // is in flight; the ⋮ menu item shows a spinner during that window.
  const [unlistBusy, setUnlistBusy] = useState(false);
  const handleToggleUnlist = async () => {
    if (unlistBusy) return;
    // Archive / Unarchive: archived prayers are hidden everywhere (feeds,
    // circle, direct links) and live only in My Prayers → Archived.
    const next = prayer.visibility !== 'archived';
    setUnlistBusy(true);
    try {
      const updated = await updatePrayer(prayer.id, { archived: next });
      // Let the parent list refresh its copy of the prayer if it
      // manages state; otherwise the card will pick up the new
      // visibility on the next feed fetch.
      if (typeof onUpdate === 'function') onUpdate(updated);
    } catch (err) {
      console.error('unlist toggle failed', err);
      alert(err?.response?.data?.detail || 'Could not update visibility. Please try again.');
    } finally {
      setUnlistBusy(false);
    }
  };

  // Owner-only navigation to the Edit form. Route matches the one the
  // Manage Prayers page uses (see App.js `/edit-prayer/:id`). Wrapped
  // in a handler that closes the ⋮ dropdown first so the transition
  // doesn't visually collide with the page navigation.
  const handleEditNavigate = () => {
    setMenuOpen(false);
    setTimeout(() => navigate(`/edit-prayer/${prayer.id}`), 10);
  };

  // ---- Menu handlers ----
  // Lazy-load the circle status the first time the ⋮ menu opens so we
  // don't fire an extra request on every card. Reuses the same
  // /circle/status endpoint the metadata-row button hits.
  const handleMenuOpenChange = async (open) => {
    setMenuOpen(open);
    if (!open || !prayer.user_id) return;
    // Circle status
    if (!circleLoaded) {
      if (circleIsSelf) {
        setCircleLoaded(true);
      } else {
        try {
          const s = await getCircle(prayer.user_id);
          setCircleInCircle(!!s.in_circle);
          setCircleIsSelf(!!s.is_self);
        } catch (err) {
          if (err?.response?.status !== 401) console.error('[Circle-menu] status fetch failed', err);
        } finally {
          setCircleLoaded(true);
        }
      }
    }
    // Pin status — only meaningful when signed in and viewing someone
    // else's prayer (own-prayer menu row is hidden regardless).
    if (!pinLoaded && user && !isOwner) {
      try {
        const s = await getPinnedStatus(prayer.id);
        setPinned(!!s.pinned);
      } catch (err) {
        if (err?.response?.status !== 401) console.error('[Pin-menu] status fetch failed', err);
      } finally {
        setPinLoaded(true);
      }
    }
  };

  const handleMenuTogglePin = async () => {
    if (!user) { openAuthModal(); return; }
    if (isOwner || pinBusy) return;
    setPinBusy(true);
    const previous = pinned;
    const next = !previous;
    setPinned(next); // optimistic
    try {
      if (next) {
        await pinPrayer(prayer.id);
      } else {
        await unpinPrayer(prayer.id);
      }
      // Notify any other card / page that shows the same prayer or
      // the Circle-page feed so it can refresh in-place.
      window.dispatchEvent(new CustomEvent('pin:changed', {
        detail: { prayerId: prayer.id, pinned: next, userId: user.user_id },
      }));
    } catch (err) {
      console.error('[Pin-menu] toggle failed', err);
      setPinned(previous); // rollback
    } finally {
      setPinBusy(false);
    }
  };

  const handleMenuToggleCircle = async (e) => {
    e?.preventDefault?.();
    if (!user) { openAuthModal(); return; }
    if (circleIsSelf || circleBusy) return;
    setCircleBusy(true);
    // Optimistic update — flip the local state immediately so the
    // menu label + heart icon respond right away. If the API call
    // fails we roll back below.
    const previous = circleInCircle;
    const next = !previous;
    setCircleInCircle(next);
    window.dispatchEvent(new CustomEvent('circle:changed', {
      detail: { authorUserId: prayer.user_id, in_circle: next },
    }));
    try {
      const s = await toggleCircle(prayer.user_id);
      const serverInCircle = !!s.in_circle;
      if (serverInCircle !== next) {
        setCircleInCircle(serverInCircle);
        window.dispatchEvent(new CustomEvent('circle:changed', {
          detail: { authorUserId: prayer.user_id, in_circle: serverInCircle },
        }));
      }
    } catch (err) {
      console.error('[Circle-menu] toggle failed', err);
      // Roll back on failure so the UI stays truthful.
      setCircleInCircle(previous);
      window.dispatchEvent(new CustomEvent('circle:changed', {
        detail: { authorUserId: prayer.user_id, in_circle: previous },
      }));
    } finally {
      setCircleBusy(false);
    }
  };

  if (deleted && typeof onDelete !== 'function') return null;

  return (
    <Link
      to={`/prayer/${prayer.id}`}
      data-testid={`prayer-card-${prayer.id}`}
      onClickCapture={(e) => {
        // While the exit animation is playing, block navigation so the
        // user doesn't get whisked to the watch page mid-shrink.
        if (closing) { e.preventDefault(); e.stopPropagation(); }
      }}
      className={`block group origin-center transition-all duration-300 ease-in ${
        closing
          ? 'opacity-0 scale-75 -translate-y-4 rotate-[-3deg] blur-[1px] pointer-events-none'
          : 'opacity-100 scale-100 translate-y-0 rotate-0'
      }`}
    >
      <div
        className="relative rounded-xl overflow-hidden aspect-video mb-2 bg-black"
        style={
          imageSrc
            ? undefined
            : { background: bgGradient(prayer.background_color) }
        }
      >
        {imageSrc ? (
          <>
            <img
              src={imageSrc}
              alt={prayer.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform"
              style={{
                // Mobile sub-pixel rendering fix — a 1px inset then a
                // 1.02x scale ensures the image visually bleeds past the
                // container edges by ~1px on every side so the container's
                // rounded corners never expose a hairline gap to the card
                // body underneath. The `bg-black` on the parent doubles
                // as a fallback if the image ever fails to cover.
                transform: 'scale(1.01)',
                transformOrigin: 'center',
              }}
            />
            {/* Title overlay — mirrors the "no image" variant's layout
                so every card in a feed reads the same regardless of
                whether the author picked a cover. A soft dark gradient
                keeps the title legible against any photo, and a heavy
                text-shadow prevents washout on light patches. Rendered
                as pointer-events-none so the wrapping <Link> still
                receives the tap. */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/70 pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6 pointer-events-none">
              <div className="text-center w-full">
                <div
                  className="font-medium line-clamp-3 text-white text-[1.6rem] sm:text-[1.15rem] leading-snug"
                  style={{
                    fontFamily: '"Times New Roman", Times, serif',
                    textShadow: '0 2px 8px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.9)',
                  }}
                >
                  {prayer.title}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center p-4 sm:p-6">
            <div className="text-center w-full">
              <div
                className="font-medium line-clamp-3 text-[1.6rem] sm:text-[1.15rem] leading-snug"
                style={{
                  fontFamily: '"Times New Roman", Times, serif',
                  color: readableTextOn(prayer.background_color),
                  textShadow: readableTextShadow(prayer.background_color),
                }}
              >
                {prayer.title}
              </div>
            </div>
          </div>
        )}
        {/* Hero author-avatar overlay — mirrors the Watch page layout so
            every prayer card reads with a unified visual language. Sized
            as EXACTLY 20% (1/5th) of the cover height via a pure CSS
            trick: cover container is `aspect-video` (16:9), so
            20% of height = width × 9 / (16 × 5) = 11.25% of width.
            Combining `w-[11.25%]` with `aspect-square rounded-full` gives
            width = height = one-fifth of the rendered hero height,
            reactive at any viewport with zero JS. */}
        {(prayer.user_avatar || prayer.user_handle || prayer.user_name) && (
          <div
            data-testid={`prayer-card-hero-author-avatar-${prayer.id}`}
            className="absolute top-2 left-2 z-10 w-[11.25%] aspect-square rounded-full overflow-hidden ring-2 ring-white/70 dark:ring-white/50 shadow-lg pointer-events-none"
          >
            {prayer.user_avatar ? (
              <img
                src={prayer.user_avatar}
                alt={prayer.user_handle ? `@${prayer.user_handle}` : (prayer.user_name || 'Author')}
                className="w-full h-full object-cover"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-white"
                style={{ background: 'rgba(0,0,0,0.55)', fontSize: '55%' }}
              >
                {(prayer.user_handle || prayer.user_name || '?').slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
        )}
        {/* LIVE praying-now badge — mirrors the Watch page hero: a small
            rose pill with a pulsing dot showing how many people are
            actively praying with this prayer RIGHT NOW. Positioned to
            the immediate RIGHT of the 20% hero author avatar so the two
            never overlap (using calc: avatar left-2 + avatar width 11.25%
            + 0.375rem gap). Hidden when the count is 0 / missing. */}
        {Number(prayer.online_praying_now_count) > 0 && (
          <div
            data-testid={`prayer-card-live-badge-${prayer.id}`}
            title={`${prayer.online_praying_now_count} praying right now`}
            className="absolute top-2 left-[calc(0.5rem+11.25%+0.375rem)] z-20 flex items-center gap-1.5 bg-rose-500/95 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shadow-lg pointer-events-none"
          >
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
            </span>
            <span data-testid={`prayer-card-live-badge-count-${prayer.id}`}>
              Live · {prayer.online_praying_now_count}
            </span>
          </div>
        )}
        {/* Included badge — shown in the BOTTOM-LEFT corner when this
            card is an included (non-own) prayer on someone's Circle
            Page. Semi-transparent circular backdrop for readability
            over any image. Kept outside the DropdownMenu tree so it
            always renders. */}
        {isPinned && (
          <div
            data-testid={`prayer-card-pinned-badge-${prayer.id}`}
            title="Included in this circle"
            aria-label="Included in this circle"
            className="absolute bottom-2 left-2 z-20 w-8 h-8 rounded-full bg-black/50 text-white backdrop-blur flex items-center justify-center pointer-events-none"
          >
            <Pin size={14} strokeWidth={2.5} fill="currentColor" className="rotate-45" />
          </div>
        )}
        {/* Ellipses menu — always-visible ⋮ affordance in the top-right of
            the thumbnail. Houses the secondary actions (Join Circle,
            Share, Light Candle, Report Misuse). Rendered outside the
            <Link>-child stack via `stopPropagation` on the trigger so
            tapping it never navigates to the watch page. Uses a
            semi-transparent circular backdrop so it stays visible on
            any image or gradient background. */}
        <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              data-testid={`prayer-card-menu-trigger-${prayer.id}`}
              aria-label="Prayer actions"
              title="Prayer actions"
              className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur flex items-center justify-center transition-colors active:scale-95"
            >
              <MoreVertical size={16} strokeWidth={2.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={6}
            className="z-[1000] min-w-[180px]"
            data-testid={`prayer-card-menu-${prayer.id}`}
            onClick={(e) => { e.stopPropagation(); }}
          >
            {showMoveControls && (
              <DropdownMenuItem
                onSelect={() => { if (canMoveUp && typeof onMoveUp === 'function') onMoveUp(prayer); }}
                disabled={!canMoveUp}
                data-testid={`menu-move-up-${prayer.id}`}
                className="cursor-pointer"
              >
                <ArrowUp size={15} className="mr-2" strokeWidth={2.25} />
                <span>Move Up</span>
              </DropdownMenuItem>
            )}
            {showMoveControls && (
              <DropdownMenuItem
                onSelect={() => { if (canMoveDown && typeof onMoveDown === 'function') onMoveDown(prayer); }}
                disabled={!canMoveDown}
                data-testid={`menu-move-down-${prayer.id}`}
                className="cursor-pointer"
              >
                <ArrowDown size={15} className="mr-2" strokeWidth={2.25} />
                <span>Move Down</span>
              </DropdownMenuItem>
            )}
            {!circleIsSelf && circleLoaded && (
              <DropdownMenuItem
                onSelect={() => { handleMenuToggleCircle(); }}
                disabled={circleBusy}
                data-testid={`menu-join-circle-${prayer.id}`}
                className="cursor-pointer"
              >
                {circleBusy ? (
                  <Loader2 size={15} className="mr-2 animate-spin" />
                ) : circleInCircle ? (
                  <Check size={15} className="mr-2" strokeWidth={2.25} />
                ) : (
                  <Users size={15} className="mr-2" strokeWidth={2.25} />
                )}
                <span>{circleInCircle ? 'Unjoin Circle' : 'Join Circle'}</span>
              </DropdownMenuItem>
            )}
            {!isOwner && pinLoaded && (
              <DropdownMenuItem
                onSelect={() => { handleMenuTogglePin(); }}
                disabled={pinBusy}
                data-testid={`menu-pin-${prayer.id}`}
                className="cursor-pointer"
              >
                {pinBusy ? (
                  <Loader2 size={15} className="mr-2 animate-spin" />
                ) : (
                  <Pin
                    size={15}
                    className={`mr-2 ${pinned ? 'text-amber-600 rotate-45' : ''}`}
                    strokeWidth={2.25}
                    fill={pinned ? 'currentColor' : 'none'}
                  />
                )}
                <span>{pinned ? 'Un-include from my circle' : 'Include in my circle'}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={() => { handleShare({ preventDefault() {}, stopPropagation() {} }); }}
              data-testid={`menu-share-${prayer.id}`}
              className="cursor-pointer"
            >
              {shareStatus === 'shared' || shareStatus === 'copied' ? (
                <Check size={15} className="mr-2" strokeWidth={2.25} />
              ) : (
                <Share2 size={15} className="mr-2" strokeWidth={2.25} />
              )}
              <span>Share</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                // Radix auto-closes the menu on select. Prevent the
                // default close so the user sees the "Link copied"
                // confirmation right where they clicked before it
                // times out on its own 1.8s later.
                if (e && typeof e.preventDefault === 'function') e.preventDefault();
                handleCopyLink();
              }}
              data-testid={`menu-copy-link-${prayer.id}`}
              className="cursor-pointer"
            >
              {copyLinkStatus === 'copied' ? (
                <Check size={15} className="mr-2 text-emerald-500" strokeWidth={2.25} />
              ) : (
                <Link2 size={15} className="mr-2" strokeWidth={2.25} />
              )}
              <span>
                {copyLinkStatus === 'copied'
                  ? 'Link copied'
                  : copyLinkStatus === 'error'
                    ? 'Copy failed'
                    : 'Copy link'}
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => { handleToggleCandle({ preventDefault() {}, stopPropagation() {} }); }}
              disabled={candleBusy}
              data-testid={`menu-candle-${prayer.id}`}
              className="cursor-pointer"
            >
              <Flame
                size={15}
                strokeWidth={2.25}
                className={`mr-2 ${isCandleLit ? 'text-orange-500 candle-lit' : 'text-amber-600'}`}
                fill={isCandleLit ? 'rgba(249, 115, 22, 0.85)' : 'rgba(251, 191, 36, 0.35)'}
              />
              <span>{isCandleLit ? 'Unlight Candle' : 'Light Candle'}</span>
            </DropdownMenuItem>
            {!isOwner && (
              <DropdownMenuItem
                onSelect={() => {
                  // Close the dropdown first (Radix auto-closes on
                  // select), then pop the report dialog on the next tick
                  // so the two transitions don't overlap.
                  if (!user) { openAuthModal(); return; }
                  setTimeout(() => setReportOpen(true), 10);
                }}
                data-testid={`menu-report-${prayer.id}`}
                className="cursor-pointer"
              >
                <Flag size={15} className="mr-2" strokeWidth={2.25} />
                <span>Report Misuse</span>
              </DropdownMenuItem>
            )}
            {onDismiss && (
              <DropdownMenuItem
                onSelect={() => {
                  // Instant close — no confirmation dialog. Trigger a
                  // shrink + pull-off animation before we let the parent
                  // remove the card from state, so it always feels like
                  // the card physically detaches into History → Closed.
                  if (closing) return;
                  setClosing(true);
                  setTimeout(() => { onDismiss(prayer); }, 360);
                }}
                data-testid={`menu-dismiss-${prayer.id}`}
                className="cursor-pointer"
              >
                <X size={15} className="mr-2" strokeWidth={2.25} />
                <span>Close</span>
              </DropdownMenuItem>
            )}
            {/* Owner-only actions. Rendered last so the shared items
                (Join / Pin / Share / Candle / Report / Close) stay in
                their existing positions for non-owners. Only visible
                when the current user IS the prayer's author — the
                Circle Page + My Prayers routes rely on this gate so
                other viewers never see these actions. */}
            {isOwner && (
              <DropdownMenuItem
                onSelect={() => {
                  // Close the dropdown first (Radix handles that on
                  // select), then flip the OwnerCommentsDialog open on
                  // the next tick so the two transitions don't visually
                  // collide. Uses the same portal-mounted modal the
                  // Watch page + Manage Prayers row menu use.
                  setTimeout(() => setCommentsOpen(true), 10);
                }}
                data-testid={`menu-owner-comment-${prayer.id}`}
                className="cursor-pointer"
              >
                <MessageCircle size={15} className="mr-2" strokeWidth={2.25} />
                <span>
                  {commentsCount > 0
                    ? `Comment (${commentsCount})`
                    : 'Comment'}
                </span>
              </DropdownMenuItem>
            )}
            {isOwner && (
              <DropdownMenuItem
                onSelect={() => { handleEditNavigate(); }}
                data-testid={`menu-owner-edit-${prayer.id}`}
                className="cursor-pointer"
              >
                <Pencil size={15} className="mr-2" strokeWidth={2.25} />
                <span>Edit</span>
              </DropdownMenuItem>
            )}
            {isOwner && (
              <DropdownMenuItem
                onSelect={() => { handleToggleUnlist(); }}
                disabled={unlistBusy}
                data-testid={`menu-owner-archive-${prayer.id}`}
                className="cursor-pointer"
              >
                {unlistBusy ? (
                  <Loader2 size={15} className="mr-2 animate-spin" />
                ) : prayer.visibility === 'archived' ? (
                  <ArchiveRestore size={15} className="mr-2" strokeWidth={2.25} />
                ) : (
                  <Archive size={15} className="mr-2" strokeWidth={2.25} />
                )}
                <span>
                  {prayer.visibility === 'archived' ? 'Unarchive' : 'Archive'}
                </span>
              </DropdownMenuItem>
            )}
            {isOwner && (
              <DropdownMenuItem
                onSelect={() => { handleDelete({ preventDefault() {}, stopPropagation() {} }); }}
                disabled={deleting}
                data-testid={`menu-owner-delete-${prayer.id}`}
                className="cursor-pointer text-rose-600 dark:text-rose-400 focus:text-rose-700 dark:focus:text-rose-300"
              >
                {deleting ? (
                  <Loader2 size={15} className="mr-2 animate-spin" />
                ) : (
                  <Trash2 size={15} className="mr-2" strokeWidth={2.25} />
                )}
                <span>Delete</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex flex-col gap-1.5">
        {(() => {
          // Prefer the author's @handle; fall back to their display name
          // only when the handle isn't set (mostly legacy accounts).
          const authorDisplay = prayer.user_handle
            ? `@${prayer.user_handle}`
            : (prayer.user_name || 'Anonymous');
          const circleHref = prayer.user_id ? `/circle/${prayer.user_id}` : null;
          const openCircle = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (circleHref) navigate(circleHref);
          };
          return (
            <>
              {/* Primary metadata row — unified layout matching the
                  Watch/Preview page: candle icon | share icon | date |
                  @handle, all on ONE horizontal line at the left. Both
                  icon buttons use identical fixed 32x32 boxes and
                  fixed 20px icons so lit/unlit only swaps colors —
                  zero reflow between states. */}
              <div
                className="flex items-center gap-2 min-w-0 text-[13px] font-medium text-zinc-700 dark:text-zinc-300 tabular-nums"
                data-testid={`prayer-card-meta-${prayer.id}`}
              >
                <button
                  type="button"
                  onClick={handleToggleCandle}
                  disabled={candleBusy}
                  data-testid={`toggle-candle-btn-${prayer.id}`}
                  data-candle-lit={isCandleLit ? 'true' : 'false'}
                  aria-pressed={isCandleLit}
                  aria-label={isCandleLit ? 'Unlight your candle' : 'Light a candle'}
                  title={isCandleLit ? 'You lit this — tap to un-light' : 'Light a candle'}
                  className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors active:scale-90 disabled:opacity-60 ${
                    isCandleLit
                      ? 'bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 text-amber-600'
                      : 'hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-600 hover:text-amber-500'
                  }`}
                >
                  <Flame
                    size={20}
                    strokeWidth={2.25}
                    className={isCandleLit ? 'text-orange-500 candle-lit' : 'text-amber-600 drop-shadow-[0_0_6px_rgba(251,146,60,0.55)]'}
                    fill={isCandleLit ? 'rgba(249, 115, 22, 0.85)' : 'rgba(251, 191, 36, 0.35)'}
                  />
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  data-testid={`share-prayer-btn-${prayer.id}`}
                  className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-600 hover:text-amber-500 relative transition-colors active:scale-90"
                  aria-label="Share prayer"
                  title="Share prayer"
                >
                  {shareStatus === 'shared' || shareStatus === 'copied'
                    ? <Check size={20} strokeWidth={2.5} className="text-amber-600" />
                    : <Share2 size={20} strokeWidth={2.25} className="text-amber-600 drop-shadow-[0_0_6px_rgba(251,146,60,0.55)]" />}
                </button>
                <span
                  className="inline-flex items-center gap-1 flex-shrink-0"
                  data-testid={`meta-date-${prayer.id}`}
                  title={new Date(prayer.created_at).toLocaleString()}
                >
                  <Calendar size={13} strokeWidth={2.25} className="text-zinc-500" />
                  {timeAgo(prayer.created_at)}
                </span>
                {circleHref ? (
                  <span
                    role="link"
                    tabIndex={0}
                    onClick={openCircle}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openCircle(e); }}
                    data-testid={`prayer-card-author-${prayer.id}`}
                    aria-label={`Open ${authorDisplay}'s prayer circle`}
                    title={`View ${authorDisplay}'s prayer circle`}
                    className="inline-flex items-center gap-1 min-w-0 truncate hover:underline cursor-pointer"
                  >
                    <span className="truncate">{authorDisplay}</span>
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 min-w-0 truncate"
                    data-testid={`prayer-card-author-${prayer.id}`}
                  >
                    <span className="truncate">{authorDisplay}</span>
                  </span>
                )}
              </div>
              {/* Random-share recipient / sender reveal — surfaces the
                  receiver's @handle only to the author viewing their own
                  outgoing fan-out, or the sender's handle to the recipient. */}
              {sharedContextRole === 'authored' && prayer.random_share_recipient_handle && (
                <div
                  data-testid={`prayer-card-recipient-${prayer.id}`}
                  className="text-xs text-sky-600 dark:text-sky-400 truncate"
                  title={`This random share was received by @${prayer.random_share_recipient_handle}`}
                >
                  → Received by <span className="font-medium">@{prayer.random_share_recipient_handle}</span>
                </div>
              )}
              {sharedContextRole === 'received' && prayer.user_handle && (
                <div
                  data-testid={`prayer-card-sender-${prayer.id}`}
                  className="text-xs text-emerald-600 dark:text-emerald-400 truncate"
                  title={`This random share was sent to you by @${prayer.user_handle}`}
                >
                  → Shared with you by <span className="font-medium">@{prayer.user_handle}</span>
                </div>
              )}
              {/* Secondary meta line — engagement counters (candles + views). */}
              <div
                className="flex items-center gap-3 text-xs font-medium text-zinc-500 dark:text-zinc-400 tabular-nums"
                data-testid={`prayer-card-counts-${prayer.id}`}
              >
                <span
                  className="inline-flex items-center gap-1"
                  data-testid={`candle-count-${prayer.id}`}
                  title={`${candlesCount} candles lit`}
                >
                  <Flame size={12} strokeWidth={2.25} className="text-amber-600" fill="rgba(251, 191, 36, 0.35)" />
                  <span>{fmtCount(candlesCount)}</span>
                  <span>{candlesCount === 1 ? 'candle' : 'candles'}</span>
                </span>
                <span
                  className="inline-flex items-center gap-1"
                  data-testid={`meta-views-${prayer.id}`}
                  title={`${prayer.plays_count || 0} views`}
                >
                  <Eye size={12} strokeWidth={2.25} className="text-zinc-500" />
                  <span>{fmtCount(prayer.plays_count)}</span>
                  <span>{(prayer.plays_count || 0) === 1 ? 'view' : 'views'}</span>
                </span>
              </div>
              {/* Hashtag pills intentionally removed from the card
                  preview — keeps the metadata row focused on
                  candles/views. Data is still returned by the API
                  and displayed on the WatchPrayer detail page. Flip
                  `SHOW_HASHTAG_UI` in AddPrayer.jsx to restore the
                  compose-form field as well. */}
            </>
          );
        })()}
      </div>
      {isOwner && commentsOpen && createPortal(
        <OwnerCommentsDialog
          open={commentsOpen}
          onClose={() => setCommentsOpen(false)}
          prayerId={prayer.id}
          canManage={true}
          onCountChange={(n) => setCommentsCount(n)}
        />,
        document.body
      )}
      {/* Report Misuse dialog rendered in a headless (no-trigger) mode so
          the ⋮ menu's "Report Misuse" row can close the dropdown first
          and then flip this open — the trigger button that ships inside
          `ReportPrayerButton` is suppressed via controlled props. */}
      {!isOwner && (
        <ReportPrayerButton
          prayerId={prayer.id}
          variant="menuitem"
          isOwner={isOwner}
          open={reportOpen}
          onOpenChange={setReportOpen}
        />
      )}
      {/* Custom branded confirmation dialog. Portaled by Radix into
          document.body, but React synthetic events still bubble
          through the portal's React parent chain — the wrapping
          <span>'s bubble-phase onClick stops those synthetic
          clicks from reaching the outer <Link>. Do NOT add an
          onClickCapture handler here: capture-phase stopPropagation
          fires BEFORE the click reaches AlertDialogCancel /
          AlertDialogAction, swallowing Radix's own close handler
          and freezing the modal open. */}
      <span
        onClick={(e) => { e.stopPropagation(); }}
      >
        {confirmDialogEl}
      </span>
    </Link>
  );
};

export default PrayerCard;
